import { Injectable } from '@angular/core';
import { CompanyInfo, DeadLetterDetail, TreatmentGroupInfo } from '../models/dead-letter-detail.model';
import { ComparedField, DeadLetterDetailSnapshot } from '../models/dead-letter-snapshot.model';

export interface DisplayComparedField extends ComparedField<string> {
  labelKey: string;
  tooltip?: string;
}

export interface CompareResult {
  company: Record<string, DisplayComparedField>;
  treatmentGroup: Record<string, DisplayComparedField>;
}

interface CompareFieldDefinition<T> {
  key: string;
  labelKey: string;
  getValue: (source: T) => unknown;
  getTooltip?: (source: T) => string;
}

@Injectable({ providedIn: 'root' })
export class DeadLetterCompareService {

  buildCompareState(detail: DeadLetterDetail, snapshot?: DeadLetterDetailSnapshot): CompareResult {
    const result: CompareResult = { company: {}, treatmentGroup: {} };

    const backendCompany = detail.enrichment?.company;
    if (backendCompany) {
      result.company = this.compareCompanyInfo(snapshot?.company ?? null, backendCompany);
    }

    const backendTg = detail.enrichment?.treatmentGroup;
    if (backendTg) {
      result.treatmentGroup = this.compareTreatmentGroupInfo(snapshot?.treatmentGroup ?? null, backendTg);
    }

    return result;
  }

  hasChanges(compareState: Record<string, DisplayComparedField>): boolean {
    return Object.values(compareState).some(f => f.changed);
  }

  private compareCompanyInfo(
    previous: CompanyInfo | null,
    current: CompanyInfo
  ): Record<string, DisplayComparedField> {
    const fields: CompareFieldDefinition<CompanyInfo>[] = [
      { key: 'name', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.NAME', getValue: value => value.name },
      { key: 'email', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.EMAIL', getValue: value => value.email },
      { key: 'phoneNumber', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.PHONE_NUMBER', getValue: value => value.phoneNumber },
      { key: 'website', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.WEBSITE', getValue: value => value.website },
      { key: 'id', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.ID', getValue: value => value.id },
      { key: 'ownerId', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.OWNER_ID', getValue: value => value.ownerId },
      { key: 'regionId', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.REGION_ID', getValue: value => value.regionId },
      { key: 'addressLine', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.ADDRESS_LINE', getValue: value => value.address?.addressLine },
      { key: 'postalCode', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.POSTAL_CODE', getValue: value => value.address?.postalCode },
      { key: 'city', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.CITY', getValue: value => value.address?.city },
      { key: 'state', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.STATE', getValue: value => value.address?.state },
      { key: 'country', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.COUNTRY', getValue: value => value.address?.country },
      {
        key: 'roles',
        labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.ROLES',
        getValue: value => this.formatRoles(value.roles),
        getTooltip: value => this.formatRoleNames(value.roles)
      }
    ];
    return this.compareFields(previous, current, fields);
  }

  private compareTreatmentGroupInfo(
    previous: TreatmentGroupInfo | null,
    current: TreatmentGroupInfo
  ): Record<string, DisplayComparedField> {
    const fields: CompareFieldDefinition<TreatmentGroupInfo>[] = [
      { key: 'name', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.NAME', getValue: value => value.name },
      { key: 'email', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.EMAIL', getValue: value => value.email },
      { key: 'id', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.ID', getValue: value => value.id },
      { key: 'companyId', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.COMPANY_ID', getValue: value => value.companyId },
      { key: 'isDefault', labelKey: 'DEAD_LETTERS.DETAIL.FIELDS.IS_DEFAULT', getValue: value => value.isDefault }
    ];
    return this.compareFields(previous, current, fields);
  }

  private compareFields<T>(
    previous: T | null,
    current: T,
    fields: CompareFieldDefinition<T>[]
  ): Record<string, DisplayComparedField> {
    const result: Record<string, DisplayComparedField> = {};
    for (const field of fields) {
      const currentVal = this.toCompareString(field.getValue(current));
      const previousVal = previous ? this.toCompareString(field.getValue(previous)) : null;
      const changed = previousVal !== null && previousVal !== currentVal;
      const tooltip = field.getTooltip ? this.toCompareString(field.getTooltip(current)) : '';
      result[field.key] = {
        labelKey: field.labelKey,
        tooltip,
        value: currentVal,
        previousValue: previousVal,
        changed
      };
    }
    return result;
  }

  private formatRoles(roles: CompanyInfo['roles'] | null | undefined): string {
    if (!roles?.length) {
      return '';
    }
    return roles
      .map(role => role.label || role.name || '')
      .filter(Boolean)
      .join(' | ');
  }

  private formatRoleNames(roles: CompanyInfo['roles'] | null | undefined): string {
    if (!roles?.length) {
      return '';
    }
    return roles
      .map(role => role.name)
      .filter((name): name is string => !!name)
      .join(' | ');
  }

  private toCompareString(value: unknown): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }
}
